import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHasPermission } from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import TextInput from '../components/TextInput';
import Checkbox from '../components/Checkbox';
import useLoginForm from '../hooks/useLoginForm';
import Dialog from '../components/Dialog';
import styles from './Login.module.css';

export default function Login() {
    const {
        email,
        setEmail,
        password,
        setPassword,
        remember,
        setRemember,
        handleSubmit,
        loading,
        error,
    } = useLoginForm();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [forgotPasswordStep, setForgotPasswordStep] = useState(null); // null, 'otp', 'newPassword'
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordError, setForgotPasswordError] = useState('');

    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const hasShowInsight = useHasPermission(PERMISSIONS.SHOW_INSIGHT);
    const navigate = useNavigate();
    const location = useLocation();

    React.useEffect(() => {
        // Clear any existing tokens when Login component mounts
        // This ensures a clean state when explicitly visiting login page
        if (location.pathname === '/login') {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
        }
    }, [location.pathname]);

    React.useEffect(() => {
        if (error) setDialogOpen(true);
    }, [error]);

    React.useEffect(() => {
        // Only redirect if we're actually on the login page and become authenticated
        if (isAuthenticated && location.pathname === '/login') {
            // Navigate to root path and let DashboardRedirect determine the correct tab based on permissions
            navigate('/', { replace: true });
        }
    }, [isAuthenticated, navigate, location.pathname]);

    // Email validation function
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Send OTP to email
    const handleForgotPassword = async () => {
        if (!isValidEmail(email)) {
            setForgotPasswordError('Please enter a valid email address');
            setDialogOpen(true);
            return;
        }

        setForgotPasswordLoading(true);
        setForgotPasswordError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/dashboard/auth/send-gmail-verification-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to send verification code');
            }

            setForgotPasswordEmail(email);
            setForgotPasswordStep('otp');
            setDialogOpen(true);
        } catch (err) {
            setForgotPasswordError(err.message);
            setDialogOpen(true);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    // Verify OTP code
    const handleVerifyOTP = async () => {
        if (!otpCode.trim()) {
            setForgotPasswordError('Please enter the verification code');
            return;
        }

        setForgotPasswordLoading(true);
        setForgotPasswordError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/dashboard/auth/verify-code`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: forgotPasswordEmail,
                    verification_code: otpCode
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Invalid verification code');
            }

            setForgotPasswordStep('newPassword');
            setOtpCode('');
        } catch (err) {
            setForgotPasswordError(err.message);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    // Reset password
    const handleResetPassword = async () => {
        if (!newPassword.trim()) {
            setForgotPasswordError('Please enter a new password');
            return;
        }

        if (newPassword !== confirmPassword) {
            setForgotPasswordError('Passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setForgotPasswordError('Password must be at least 6 characters long');
            return;
        }

        setForgotPasswordLoading(true);
        setForgotPasswordError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}api/v1/dashboard/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: forgotPasswordEmail,
                    new_password: newPassword,
                    confirm_password: confirmPassword
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to reset password');
            }

            // Success - close dialog and reset states
            setDialogOpen(false);
            setForgotPasswordStep(null);
            setForgotPasswordEmail('');
            setNewPassword('');
            setConfirmPassword('');
            setOtpCode('');

            // Show success message (optional)
            alert('Password reset successfully! You can now login with your new password.');
        } catch (err) {
            setForgotPasswordError(err.message);
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    // Close forgot password dialog
    const closeForgotPasswordDialog = () => {
        setDialogOpen(false);
        setForgotPasswordStep(null);
        setForgotPasswordEmail('');
        setForgotPasswordError('');
        setOtpCode('');
        setNewPassword('');
        setConfirmPassword('');
    };

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} className={styles.form}>
                <h2 className={styles.heading}>Login</h2>
                <TextInput
                    label="Email"
                    id="email"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                />
                <TextInput
                    label="Password"
                    id="password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                />
                <button
                    type="button"
                    className={styles.forgotPasswordLink}
                    onClick={handleForgotPassword}
                    disabled={forgotPasswordLoading}
                >
                    {forgotPasswordLoading ? 'Sending...' : 'Forgot Password?'}
                </button>
                <Checkbox
                    label="Remember me"
                    id="remember"
                    checked={remember}
                    onChange={e => setRemember(e.target.checked)}
                />
                <button type="submit" className={styles.button} disabled={loading}>
                    {loading && <span className={styles.spinner}></span>}
                    {loading ? 'Logging in...' : 'Login'}
                </button>
            </form>

            {/* Login Error Dialog */}
            <Dialog
                open={!!error && dialogOpen && !forgotPasswordStep && !forgotPasswordError}
                onClose={() => setDialogOpen(false)}
                title="Login Error"
            >
                {error}
            </Dialog>

            {/* Forgot Password Dialogs */}
            <Dialog
                open={(!!forgotPasswordStep || !!forgotPasswordError) && dialogOpen}
                onClose={closeForgotPasswordDialog}
                title={
                    forgotPasswordStep === 'otp' ? 'Enter Verification Code' :
                        forgotPasswordStep === 'newPassword' ? 'Reset Password' :
                            'Forgot Password'
                }
            >
                <div className={styles.dialogContent}>
                    {forgotPasswordError && (
                        <div className={styles.errorMessage}>
                            {forgotPasswordError}
                        </div>
                    )}

                    {forgotPasswordStep === 'otp' && (
                        <>
                            <p>We've sent a verification code to {forgotPasswordEmail}</p>
                            <TextInput
                                label="Verification Code"
                                type="text"
                                value={otpCode}
                                onChange={e => setOtpCode(e.target.value)}
                                placeholder="Enter 4-digit code"
                                maxLength="4"
                            />
                            <div className={styles.dialogActions}>
                                <button
                                    type="button"
                                    onClick={closeForgotPasswordDialog}
                                    className={styles.cancelButton}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleVerifyOTP}
                                    className={styles.button}
                                    disabled={forgotPasswordLoading}
                                >
                                    {forgotPasswordLoading ? 'Verifying...' : 'Verify'}
                                </button>
                            </div>
                        </>
                    )}

                    {forgotPasswordStep === 'newPassword' && (
                        <>
                            <p>Please enter your new password</p>
                            <TextInput
                                label="New Password"
                                type="password"
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                            />
                            <TextInput
                                label="Confirm Password"
                                type="password"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                            />
                            <div className={styles.dialogActions}>
                                <button
                                    type="button"
                                    onClick={closeForgotPasswordDialog}
                                    className={styles.cancelButton}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleResetPassword}
                                    className={styles.button}
                                    disabled={forgotPasswordLoading}
                                >
                                    {forgotPasswordLoading ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </div>
                        </>
                    )}

                    {!forgotPasswordStep && forgotPasswordError && (
                        <div className={styles.dialogActions}>
                            <button
                                type="button"
                                onClick={closeForgotPasswordDialog}
                                className={styles.button}
                            >
                                OK
                            </button>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
} 